import Checkbox from 'components/inputs/Checkbox';
import noop from 'lodash/noop';
import React, { CSSProperties, useEffect, useState } from 'react';
import { Sheet } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import { getPageThumbnailUrl } from 'services/pageService';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator, useDateFormatter } from '@bluebeam/react-i18n';

interface Props extends WithAppStoreProps {
  sheet: Sheet;
  selected: boolean;
  showCheckbox?: boolean;
  onSheetClick?: (sheet: Sheet) => void;
  onCheckboxChange?: (
    sheet: Sheet,
    clicked: boolean,
    evt: React.SyntheticEvent
  ) => void;
  style?: CSSProperties;
}

const localeOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

const SheetTile = ({
  showCheckbox = false,
  onSheetClick = noop,
  onCheckboxChange = noop,
  sheet,
  selected,
  style,
}: Props) => {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.2,
  };

  const tileRef: React.RefObject<HTMLDivElement> = React.createRef();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');

  const { translate } = useTranslator(['drawingsPage']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'long',
    timeStyle: 'short',
  });

  useEffect(() => {
    const observer: IntersectionObserver = new IntersectionObserver(
      observerCallback,
      options
    );
    if (tileRef.current) {
      observer.observe(tileRef.current);
    }
  }, []);

  const observerCallback = (
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver
  ): void => {
    entries.forEach(async (entry) => {
      if (entry.isIntersecting) {
        const latestSheetVersion = sheet.versions.last<SheetVersion>();

        const url = await getPageThumbnailUrl(
          latestSheetVersion.documentid,
          latestSheetVersion.pageid
        );

        setLoaded(true);
        setUrl(url);

        if (tileRef.current) {
          observer.unobserve(tileRef.current as HTMLDivElement);
        }
      }
    });
  };

  const handlePageClick = (): void => {
    if (onSheetClick) {
      onSheetClick(sheet);
    }
  };

  const handleCheckboxChange = (
    checked: boolean,
    evt: React.SyntheticEvent
  ): void => {
    evt.stopPropagation();

    if (onCheckboxChange) {
      onCheckboxChange(sheet, checked, evt);
    }
  };

  const latestSheetVersion = sheet.versions.last<SheetVersion>();
  const date = new Date(latestSheetVersion.uploadtimestamp * 1000);
  const id = `sheet-tile-${latestSheetVersion.pageid}`;

  return (
    <div
      id={id}
      style={style}
      ref={tileRef}
      data-testid="sheet-tile-item"
      className="b-2 h100 pos-r cursor-pointer bgd-white oh"
      onClick={handlePageClick}
    >
      {selected ? (
        <div
          className="pos-a w100 h100 bgd-primary-lighter"
          style={{ opacity: 0.1 }}
        />
      ) : null}
      {showCheckbox ? (
        <div className="pos-a" style={{ top: '8px', left: '8px' }}>
          <Checkbox
            id={`${id}-checkbox`}
            checked={selected}
            onChange={handleCheckboxChange}
            size="large"
          />
        </div>
      ) : null}
      <div
        className="w100"
        title={translate('drawingsPage:uploadedByUser', {
          uploaderEmailAddress: latestSheetVersion.uploadedBy,
          dateTime: dateFormatter.format(date),
        })}
      >
        {loaded ? (
          <img className="w100" alt={latestSheetVersion.pageid} src={url} />
        ) : null}
      </div>
      <div
        className="pos-a w100 bgd-white bt-2"
        style={{ bottom: '0px', left: '0px' }}
      >
        <div className="column pos-r px2 py1">
          {selected ? (
            <div
              className="pos-a w100 h100 bgd-primary-lighter"
              style={{ opacity: 0.1, top: 0, left: 0 }}
            />
          ) : null}
          <div className="row justify-between">
            <div
              id="sheet-number"
              className="font-size-5 medium color-primary truncate"
            >
              {latestSheetVersion.sheetnumber}
            </div>
            {sheet.versions.size > 1 ? (
              <div className="flex-center">
                <div
                  className="color-gray-6 b-width-2 b-2 px1"
                  style={{
                    fontSize: '10px',
                    borderRadius: '4px',
                    padding: '2px 5px',
                  }}
                >
                  <div>R{sheet.versions.size - 1}</div>
                </div>
              </div>
            ) : null}
          </div>
          <div
            id="sheet-title"
            className="mt1 color-gray-6 font-size-2 ellipsis"
          >
            {latestSheetVersion.sheettitle}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAppStore<Props>(SheetTile);
